import { useState, useEffect, useRef } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { jobsApi } from '@/services/api/jobs'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Pagination } from '@/components/ui/pagination'
import { X, Loader2, BarChart3, Calendar, Database, ArrowLeft, Trash2 } from 'lucide-react'
import { formatDistanceToNow } from 'date-fns'
import BacktestResultsView from './BacktestResultsView'
import { Badge } from '@/components/ui/badge'
import { cn } from '@/lib/utils'
import { showSuccessToast, showErrorToast } from '@/utils/error-handler'

interface BacktestTabsProps {
  scriptId: string
  onClose?: () => void
  initialJobId?: string | null
}

export default function BacktestTabs({ scriptId, onClose, initialJobId }: BacktestTabsProps) {
  const [selectedJobId, setSelectedJobId] = useState<string | null>(initialJobId || null)
  const [showGrid, setShowGrid] = useState(!initialJobId)
  const [jobsPage, setJobsPage] = useState(1)
  const [jobsPageSize, setJobsPageSize] = useState(9) // 3x3 grid
  const [deletingJobId, setDeletingJobId] = useState<string | null>(null)
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [jobToDelete, setJobToDelete] = useState<string | null>(null)
  const hasInitialized = useRef(false)
  const queryClient = useQueryClient()

  // Fetch all backtest jobs (completed, processing, queued)
  const { data: backtestJobs, isLoading, refetch: refetchBacktestJobs } = useQuery({
    queryKey: ['backtest-jobs', scriptId],
    queryFn: async () => {
      const [completed, processing, queued] = await Promise.all([
        jobsApi.listByScriptId(scriptId, { jobType: 'backtest', status: 'completed', limit: 20 }),
        jobsApi.listByScriptId(scriptId, { jobType: 'backtest', status: 'processing', limit: 10 }),
        jobsApi.listByScriptId(scriptId, { jobType: 'backtest', status: 'queued', limit: 10 }),
      ])
      // Combine and sort by createdAt descending (newest first)
      const all = [...queued, ...processing, ...completed]
      return all.sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      )
    },
    enabled: !!scriptId,
    refetchInterval: (query) => {
      // Poll if there are jobs in progress
      const data = query.state.data
      if (!Array.isArray(data)) {
        return false
      }
      const hasActiveJobs = data.some(job => 
        job.status === 'queued' || job.status === 'processing'
      )
      return hasActiveJobs ? 3000 : false // Poll every 3 seconds if active
    },
  })

  // Auto-select initialJobId when provided
  useEffect(() => {
    if (initialJobId && initialJobId !== selectedJobId) {
      setSelectedJobId(initialJobId)
      setShowGrid(false)
    }
  }, [initialJobId])

  // Auto-select job in progress if initialJobId is provided and job exists
  useEffect(() => {
    if (backtestJobs && initialJobId && !hasInitialized.current) {
      const job = backtestJobs.find(j => j.id === initialJobId)
      if (job) {
        setSelectedJobId(initialJobId)
        setShowGrid(false)
        hasInitialized.current = true
      }
    }
  }, [backtestJobs, initialJobId])

  // Collapse grid when a job is selected
  useEffect(() => {
    if (selectedJobId) {
      setShowGrid(false)
    }
  }, [selectedJobId])

  // Fetch selected job directly to get real-time updates (especially for jobs in progress)
  // IMPORTANT: This hook must be before any conditional returns to follow Rules of Hooks
  const { data: selectedJobData, isLoading: isLoadingSelectedJob } = useQuery({
    queryKey: ['job', selectedJobId],
    queryFn: () => jobsApi.get(selectedJobId!),
    enabled: !!selectedJobId && selectedJobId !== undefined,
    refetchInterval: (query) => {
      const jobData = query.state.data
      // Poll if job is active
      if (jobData?.status === 'queued' || jobData?.status === 'processing') {
        return 3000 // Poll every 3 seconds for active jobs
      }
      // Stop polling for completed/failed jobs
      return false
    },
  })

  // Track previous status to detect completion
  const previousStatusRef = useRef<string | undefined>(undefined)

  const selectedJob = backtestJobs?.find(job => job.id === selectedJobId)
  const displayJob = selectedJobData || selectedJob

  // Detect when job completes and show toast
  useEffect(() => {
    const currentJob = displayJob
    if (!currentJob) return

    const previousStatus = previousStatusRef.current
    const currentStatus = currentJob.status

    // Update ref for next comparison
    previousStatusRef.current = currentStatus

    // Show success toast when job transitions to completed
    if (
      previousStatus &&
      (previousStatus === 'queued' || previousStatus === 'processing') &&
      currentStatus === 'completed'
    ) {
      showSuccessToast('Backtest completed successfully')
      // Refetch the backtest jobs list to include the completed job
      refetchBacktestJobs()
    }

    // Show error toast when job fails
    if (
      previousStatus &&
      (previousStatus === 'queued' || previousStatus === 'processing') &&
      currentStatus === 'failed'
    ) {
      showErrorToast(
        new Error(currentJob.error || 'Backtest failed'),
        'Backtest failed'
      )
    }
  }, [displayJob?.status, refetchBacktestJobs])

  const handleBackToGrid = () => {
    setSelectedJobId(null)
    setShowGrid(true)
  }

  const handleDeleteClick = (jobId: string, e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation()
      e.preventDefault()
    }
    
    const job = backtestJobs?.find(j => j.id === jobId) || displayJob
    const canDelete = job?.status === 'completed' || job?.status === 'failed' || job?.status === 'expired' || job?.status === 'cancelled'
    
    if (!canDelete) {
      showErrorToast('Only completed, failed, expired, or cancelled backtests can be deleted')
      return
    }

    setJobToDelete(jobId)
    setShowDeleteDialog(true)
  }

  const handleDeleteConfirm = async () => {
    if (!jobToDelete) return

    setDeletingJobId(jobToDelete)
    setShowDeleteDialog(false)
    
    try {
      // Delete the job and wait for confirmation
      await jobsApi.delete(jobToDelete)
      
      // If deleting the selected job, go back to grid
      if (selectedJobId === jobToDelete) {
        setSelectedJobId(null)
        setShowGrid(true)
      }
      
      // Remove the deleted job from the cache immediately (optimistic update)
      queryClient.setQueryData(['backtest-jobs', scriptId], (oldData: typeof backtestJobs) => {
        if (!Array.isArray(oldData)) return oldData
        return oldData.filter(job => job.id !== jobToDelete)
      })
      
      // Remove the job query from cache
      queryClient.removeQueries({ queryKey: ['job', jobToDelete] })
      
      // Invalidate queries to trigger refetch from server
      queryClient.invalidateQueries({ 
        queryKey: ['backtest-jobs', scriptId],
        refetchType: 'active'
      })
      
      // Small delay to ensure backend transaction is committed
      await new Promise(resolve => setTimeout(resolve, 300))
      
      // Force a fresh refetch from server (bypass any cache)
      await refetchBacktestJobs()
      
      showSuccessToast('Backtest deleted successfully')
    } catch (error) {
      console.error('Failed to delete backtest:', error)
      showErrorToast(error instanceof Error ? error : new Error('Failed to delete backtest'))
      
      // On error, refetch to restore correct state
      await refetchBacktestJobs()
    } finally {
      setDeletingJobId(null)
      setJobToDelete(null)
    }
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-8">
        <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (!backtestJobs || backtestJobs.length === 0) {
    return (
      <div className="text-center py-8 text-muted-foreground">
        <BarChart3 className="w-12 h-12 mx-auto mb-4 opacity-50" />
        <p>No backtests found for this script.</p>
        <p className="text-sm mt-2">Run a backtest to see results here.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold">Backtest Results</h3>
        {onClose && (
          <Button
            variant="ghost"
            size="sm"
            onClick={onClose}
            className="h-8 w-8 p-0"
          >
            <X className="w-4 h-4" />
          </Button>
        )}
      </div>

      {showGrid ? (
        <div className="space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {backtestJobs
              .slice((jobsPage - 1) * jobsPageSize, jobsPage * jobsPageSize)
              .map((job) => {
              const isActive = job.status === 'queued' || job.status === 'processing'
              const canDelete = job.status === 'completed' || job.status === 'failed' || job.status === 'expired' || job.status === 'cancelled'
              const isDeleting = deletingJobId === job.id
              
              return (
                <Card
                  key={job.id}
                  className={cn(
                    "cursor-pointer transition-all hover:shadow-md relative",
                    isActive && "ring-2 ring-primary/50"
                  )}
                  onClick={() => !isDeleting && setSelectedJobId(job.id)}
                >
                  <CardHeader className="pb-3">
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <CardTitle className="text-base flex items-center gap-2">
                          {isActive ? (
                            <Loader2 className="w-4 h-4 text-primary animate-spin" />
                          ) : (
                            <Database className="w-4 h-4 text-muted-foreground" />
                          )}
                          {job.dataset?.name || 'Unknown Dataset'}
                        </CardTitle>
                        <CardDescription className="mt-1 flex items-center gap-2 text-xs">
                          <Calendar className="w-3 h-3" />
                          {formatDistanceToNow(new Date(job.completedAt || job.createdAt), { addSuffix: true })}
                        </CardDescription>
                      </div>
                      {canDelete && (
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-6 w-6 text-muted-foreground hover:text-destructive"
                          onClick={(e) => handleDeleteClick(job.id, e)}
                          disabled={isDeleting}
                        >
                          {isDeleting ? (
                            <Loader2 className="w-3.5 h-3.5 animate-spin" />
                          ) : (
                            <Trash2 className="w-3.5 h-3.5" />
                          )}
                        </Button>
                      )}
                    </div>
                  </CardHeader>
                  <CardContent className="pt-0">
                    <div className="flex items-center justify-between text-xs text-muted-foreground">
                      <span>Job ID: {job.id.slice(0, 8)}...</span>
                      <Badge 
                        variant={isActive ? "default" : "outline"} 
                        className="text-xs"
                      >
                        {isActive && <Loader2 className="w-3 h-3 mr-1 animate-spin" />}
                        {job.status}
                      </Badge>
                    </div>
                    {job.completedAt && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        {new Date(job.completedAt).toLocaleString()}
                      </div>
                    )}
                    {isActive && job.progress !== undefined && (
                      <div className="mt-2 text-xs text-muted-foreground">
                        Progress: {job.progress}%
                      </div>
                    )}
                  </CardContent>
                </Card>
              )
            })}
          </div>
          {backtestJobs.length > jobsPageSize && (
            <Pagination
              currentPage={jobsPage}
              totalPages={Math.ceil(backtestJobs.length / jobsPageSize)}
              pageSize={jobsPageSize}
              totalItems={backtestJobs.length}
              onPageChange={(page) => {
                setJobsPage(page)
                window.scrollTo({ top: 0, behavior: 'smooth' })
              }}
              onPageSizeChange={(pageSize) => {
                setJobsPageSize(pageSize)
                setJobsPage(1)
              }}
              showPageSizeSelector={true}
              pageSizeOptions={[9, 18, 27, 36]}
              className="mt-4"
            />
          )}
        </div>
      ) : selectedJobId && (displayJob || isLoadingSelectedJob) ? (
        <div className="space-y-4">
          {isLoadingSelectedJob && !displayJob ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="w-5 h-5 animate-spin text-muted-foreground" />
            </div>
          ) : displayJob ? (
            <>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleBackToGrid}
                    className="h-8"
                  >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    Back to List
                  </Button>
                  <div>
                    <h4 className="font-medium">{displayJob.dataset?.name || 'Unknown Dataset'}</h4>
                    <p className="text-sm text-muted-foreground">
                      {formatDistanceToNow(new Date(displayJob.completedAt || displayJob.createdAt), { addSuffix: true })}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">
                    {displayJob.status}
                  </Badge>
                  {(displayJob.status === 'completed' || displayJob.status === 'failed' || displayJob.status === 'expired' || displayJob.status === 'cancelled') && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDeleteClick(displayJob.id)}
                      disabled={deletingJobId === displayJob.id}
                      className="h-8 text-muted-foreground hover:text-destructive"
                    >
                      {deletingJobId === displayJob.id ? (
                        <>
                          <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                          Deleting...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4 mr-2" />
                          Delete
                        </>
                      )}
                    </Button>
                  )}
                </div>
              </div>

              {displayJob.status === 'completed' ? (
                <BacktestResultsView jobId={displayJob.id} />
              ) : displayJob.status === 'processing' || displayJob.status === 'queued' ? (
                <div className="text-center py-8">
                  <Loader2 className="w-8 h-8 animate-spin text-primary mx-auto mb-4" />
                  <p className="text-muted-foreground font-medium">Backtest is {displayJob.status}</p>
                  <p className="text-sm text-muted-foreground mt-2">
                    Results will appear here when completed.
                  </p>
                  {displayJob.progress !== undefined && (
                    <p className="text-xs text-muted-foreground mt-2">
                      Progress: {displayJob.progress}%
                    </p>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-muted-foreground">
                  Backtest is {displayJob.status}. Results will appear here when completed.
                </div>
              )}
            </>
          ) : null}
        </div>
      ) : selectedJobId ? (
        <div className="text-center py-8 text-muted-foreground">
          <Loader2 className="w-5 h-5 animate-spin mx-auto mb-2" />
          <p>Loading backtest job...</p>
        </div>
      ) : null}

      {/* Delete Confirmation Dialog */}
      <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Delete Backtest</DialogTitle>
            <DialogDescription>
              Are you sure you want to delete this backtest? It will be permanently removed after 24 hours.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => {
                setShowDeleteDialog(false)
                setJobToDelete(null)
              }}
              disabled={deletingJobId !== null}
            >
              Cancel
            </Button>
            <Button 
              variant="destructive" 
              onClick={handleDeleteConfirm}
              disabled={deletingJobId !== null}
            >
              {deletingJobId ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Deleting...
                </>
              ) : (
                'Delete'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}
